"use client";

import Link from "next/link";
import { useCallback, useState } from "react";
import { buildExploreToolHrefForProduct } from "../_lib/explore-detail-hrefs";
import type { ExploreBriefCard, ExploreStoryCard, ProductRef } from "../types";
import { buildShareOnXUrl } from "./build-share-text";
import { StoryTOCDesktop, type StoryTOCItem } from "./story-toc";
import { useBriefRowTracking } from "./use-brief-analytics";
import { useStickyAside } from "./use-sticky-aside";

function SidebarBriefRow({
  brief,
  positionInList,
}: {
  brief: ExploreBriefCard;
  positionInList: number;
}) {
  const { ref } = useBriefRowTracking({
    brief,
    surface: "story_detail_related_briefs",
    positionInList,
  });
  return (
    <div className="flex gap-2.5 px-2 py-2.5" ref={ref}>
      <div className="mt-0.5 w-[3px] shrink-0 rounded-full bg-[var(--explore-border)]" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="font-semibold text-[0.8rem] text-[var(--explore-text-primary)] leading-snug">
          {brief.title}
        </span>
        <span className="text-[0.65rem] text-[var(--explore-text-muted)]">
          {brief.briefType.toLowerCase()}
          {brief.categoryEmoji ? ` ${brief.categoryEmoji}` : ""}
        </span>
      </div>
    </div>
  );
}

interface ArticleRailProps {
  deck: string;
  exploreBase: string;
  headline: string;
  productMentions: ProductRef[];
  sidebarBriefs: ExploreBriefCard[];
  sidebarStories: ExploreStoryCard[];
  storyUrl: string;
  tocItems: StoryTOCItem[];
}

export function ArticleRail({
  deck,
  exploreBase,
  headline,
  productMentions,
  sidebarBriefs,
  sidebarStories,
  storyUrl,
  tocItems,
}: ArticleRailProps) {
  const { ref, stickyTop } = useStickyAside({ mode: "fixed" });
  const [copied, setCopied] = useState(false);

  const shareOnXUrl = buildShareOnXUrl({ headline, deck, storyUrl });

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(storyUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // clipboard write can fail silently in some contexts
      }
    );
  }, [storyUrl]);

  return (
    <aside
      className="z-10 hidden flex-col gap-6 self-start overflow-y-auto rounded-2xl bg-[var(--explore-bg-sidebar)] p-4 lg:sticky lg:flex"
      ref={ref}
      style={{
        top: stickyTop,
        fontFamily: "var(--homepage-sans-stack)",
        maxHeight:
          "calc(100dvh - var(--explore-sticky-top) - var(--explore-sticky-bottom-gap))",
      }}
    >
      {/* Table of contents */}
      <StoryTOCDesktop items={tocItems} />

      {/* Share section */}
      <nav
        aria-label="Share this story"
        className="border-[var(--explore-border-light)] border-b pb-5"
      >
        <div className="mb-3 font-bold text-[0.65rem] text-[var(--explore-text-muted)] uppercase tracking-[0.08em]">
          Share
        </div>
        <div className="flex flex-col gap-2">
          <a
            className="flex min-h-11 items-center gap-3 rounded-lg border border-transparent bg-[var(--explore-bg)] px-3 py-3 font-medium text-[0.85rem] text-[var(--explore-text-secondary)] no-underline transition-all hover:border-[var(--explore-text-secondary)] hover:bg-[var(--explore-bg-card)] hover:text-[var(--explore-text-primary)]"
            href={shareOnXUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            <svg
              className="h-[18px] w-[18px] shrink-0"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Share on X
          </a>
          <button
            className={`flex min-h-11 items-center gap-3 rounded-lg border border-transparent px-3 py-3 font-medium text-[0.85rem] transition-all ${
              copied
                ? "bg-[var(--explore-accent-teal)]/10 text-[var(--explore-accent-teal)]"
                : "bg-[var(--explore-bg)] text-[var(--explore-text-secondary)] hover:border-[var(--explore-text-secondary)] hover:bg-[var(--explore-bg-card)] hover:text-[var(--explore-text-primary)]"
            }`}
            onClick={handleCopy}
            type="button"
          >
            {copied ? (
              <svg
                className="h-[18px] w-[18px] shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : (
              <svg
                className="h-[18px] w-[18px] shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            )}
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      </nav>

      {/* Products Mentioned section */}
      {productMentions.length > 0 && (
        <section aria-label="Products mentioned in this story">
          <div className="mb-3 font-bold text-[0.65rem] text-[var(--explore-text-muted)] uppercase tracking-[0.08em]">
            Products Mentioned
          </div>
          <div className="flex flex-col gap-2">
            {productMentions.map((product) => (
              <Link
                className="flex items-center gap-2.5 rounded-lg p-2 no-underline transition-colors hover:bg-[var(--explore-bg)]"
                href={buildExploreToolHrefForProduct({
                  exploreBase,
                  product,
                })}
                key={product.slug}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--explore-bg)]">
                  {product.emoji ?? product.displayName.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-[0.85rem] text-[var(--explore-text-primary)]">
                    {product.displayName}
                  </div>
                  <div className="text-[0.7rem] text-[var(--explore-text-muted)]">
                    {product.entityType.toLowerCase().replace("_", " ")}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {(sidebarStories.length > 0 || sidebarBriefs.length > 0) && (
        <div className="border-[var(--explore-border-light)] border-t pt-5">
          <div className="mb-3 font-bold text-[0.65rem] text-[var(--explore-text-muted)] uppercase tracking-[0.08em]">
            On This Topic
          </div>
          <div className="flex flex-col gap-0.5">
            {sidebarStories.map((sidebarStory) => (
              <Link
                className="group flex gap-2.5 rounded-lg px-2 py-2.5 no-underline transition-colors hover:bg-[var(--explore-bg)]"
                href={`${exploreBase}/stories/${sidebarStory.storyId}`}
                key={sidebarStory.storyId}
              >
                <div
                  className="mt-0.5 w-[3px] shrink-0 rounded-full"
                  style={{
                    backgroundColor: `var(--explore-badge-${sidebarStory.storyBadge}-bg)`,
                  }}
                />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-semibold text-[0.8rem] text-[var(--explore-text-primary)] leading-snug group-hover:text-[var(--explore-accent-teal)]">
                    {sidebarStory.headline}
                  </span>
                  <span className="text-[0.65rem] text-[var(--explore-text-muted)]">
                    {sidebarStory.storyBadge} &middot; {sidebarStory.timeAgo}
                  </span>
                </div>
              </Link>
            ))}
            {sidebarBriefs.map((brief, idx) => (
              <SidebarBriefRow
                brief={brief}
                key={brief.briefId}
                positionInList={idx}
              />
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
